/**
 * LinguistPro Elite - Model Configuration
 * Defines available LLM providers and models with capabilities
 *
 * MODEL CURRENCY — swept 2026-09-14. Every Google and Anthropic ID below was
 * checked against the live API on that date: Google IDs by a real
 * `generateContent` call (a `/v1beta/models` listing lookup is NOT evidence —
 * Google keeps retired models listed long after they start returning 404),
 * Anthropic IDs against the live `/v1/models` endpoint. The Groq LLM ID was
 * checked against Groq's live model list.
 *
 * ⚠ The OpenAI entries could NOT be verified in that sweep (no working key was
 * available) and are therefore UNVERIFIED, not confirmed-current — `gpt-4o`,
 * `gpt-4o-mini` and `gpt-4-turbo` are carried forward unchanged rather than
 * guessed at. Verify before relying on them.
 *
 * The IDs the product actually calls on the server live in `lib/models.ts`.
 * This catalogue is the user-facing picker; keep both honest.
 */

export type ModelProvider = 'google' | 'openai' | 'anthropic' | 'groq' | 'local';

export interface ModelConfig {
  id: string;
  name: string;
  provider: ModelProvider;
  modelId: string;
  description: string;
  capabilities: {
    nativeAudio: boolean;
    nativeVideo: boolean;
    maxContextTokens: number;
    speedRating: 'fast' | 'medium' | 'slow';
    costTier: 'free' | 'low' | 'medium' | 'high';
  };
  recommended: boolean;
  requiresAuth: boolean;
  authType: 'api_key' | 'oauth' | 'local' | 'none';
  rank: number; // Lower is better for transcription work
}

export interface ProviderConfig {
  id: ModelProvider;
  name: string;
  description: string;
  authInstructions: string;
  keyUrl?: string;
  models: ModelConfig[];
}

// Model definitions ranked by suitability for transcription/analysis
export const MODELS: ModelConfig[] = [
  // Google Models
  {
    id: 'gemini-3.8-flash',
    name: 'Gemini 3.8 Flash',
    provider: 'google',
    modelId: 'gemini-3.8-flash',
    description: 'Fast multimodal model with native audio/video support. Current default for transcription and AI intelligence.',
    capabilities: {
      nativeAudio: true,
      nativeVideo: true,
      maxContextTokens: 1048576,
      speedRating: 'fast',
      costTier: 'low',
    },
    recommended: true,
    requiresAuth: true,
    authType: 'api_key',
    rank: 1,
  },
  {
    id: 'gemini-3.5-flash-lite',
    name: 'Gemini 3.5 Flash-Lite',
    provider: 'google',
    modelId: 'gemini-3.5-flash-lite',
    description: 'Lightweight version for simple transcriptions. Fastest and cheapest option.',
    capabilities: {
      nativeAudio: true,
      nativeVideo: true,
      maxContextTokens: 1048576,
      speedRating: 'fast',
      costTier: 'free',
    },
    recommended: false,
    requiresAuth: true,
    authType: 'api_key',
    rank: 2,
  },

  // OpenAI Models
  {
    id: 'gpt-4o',
    name: 'GPT-4o',
    provider: 'openai',
    modelId: 'gpt-4o',
    description: 'OpenAI flagship multimodal. Excellent accuracy and reasoning capabilities.',
    capabilities: {
      nativeAudio: true,
      nativeVideo: true,
      maxContextTokens: 128000,
      speedRating: 'medium',
      costTier: 'high',
    },
    recommended: false,
    requiresAuth: true,
    authType: 'api_key',
    rank: 4,
  },
  {
    id: 'gpt-4o-mini',
    name: 'GPT-4o Mini',
    provider: 'openai',
    modelId: 'gpt-4o-mini',
    description: 'Cost-effective multimodal model. Good balance of speed and quality.',
    capabilities: {
      nativeAudio: true,
      nativeVideo: true,
      maxContextTokens: 128000,
      speedRating: 'fast',
      costTier: 'low',
    },
    recommended: false,
    requiresAuth: true,
    authType: 'api_key',
    rank: 5,
  },
  {
    id: 'gpt-4-turbo',
    name: 'GPT-4 Turbo',
    provider: 'openai',
    modelId: 'gpt-4-turbo',
    description: 'Previous generation with vision. Reliable for image-based speaker ID.',
    capabilities: {
      nativeAudio: false,
      nativeVideo: true,
      maxContextTokens: 128000,
      speedRating: 'medium',
      costTier: 'high',
    },
    recommended: false,
    requiresAuth: true,
    authType: 'api_key',
    rank: 6,
  },

  // Anthropic Models
  {
    id: 'claude-sonnet-5',
    name: 'Claude Sonnet 5',
    provider: 'anthropic',
    modelId: 'claude-sonnet-5',
    description: 'Best-in-class reasoning. Requires Whisper for audio transcription.',
    capabilities: {
      nativeAudio: false,
      nativeVideo: true,
      maxContextTokens: 200000,
      speedRating: 'fast',
      costTier: 'medium',
    },
    recommended: false,
    requiresAuth: true,
    authType: 'api_key',
    rank: 7,
  },
  {
    id: 'claude-opus-5',
    name: 'Claude Opus 5',
    provider: 'anthropic',
    modelId: 'claude-opus-5',
    description: 'Most capable Claude model. Premium quality analysis and synthesis.',
    capabilities: {
      nativeAudio: false,
      nativeVideo: true,
      maxContextTokens: 200000,
      speedRating: 'slow',
      costTier: 'high',
    },
    recommended: false,
    requiresAuth: true,
    authType: 'api_key',
    rank: 8,
  },

  // Groq Models (Ultra-fast inference)
  {
    id: 'groq-whisper-qwen',
    name: 'Groq Whisper + Qwen 3.8',
    provider: 'groq',
    modelId: 'whisper-large-v3-turbo+qwen/qwen3.8-27b',
    description: 'Ultra-fast inference. Whisper for transcription, Qwen for analysis.',
    capabilities: {
      nativeAudio: true,
      nativeVideo: false,
      maxContextTokens: 128000,
      speedRating: 'fast',
      costTier: 'low',
    },
    recommended: false,
    requiresAuth: true,
    authType: 'api_key',
    rank: 9,
  },

  // Local Models (Ollama)
  {
    id: 'ollama-whisper-llama',
    name: 'Local: Whisper + Llama 3.2',
    provider: 'local',
    modelId: 'whisper:large-v3+llama3.2:latest',
    description: 'Fully local processing. No API costs, complete privacy.',
    capabilities: {
      nativeAudio: true,
      nativeVideo: false,
      maxContextTokens: 128000,
      speedRating: 'medium',
      costTier: 'free',
    },
    recommended: false,
    requiresAuth: false,
    authType: 'local',
    rank: 10,
  },
  {
    id: 'ollama-whisper-mistral',
    name: 'Local: Whisper + Mistral',
    provider: 'local',
    modelId: 'whisper:large-v3+mistral:latest',
    description: 'Local processing with Mistral. Good for analysis tasks.',
    capabilities: {
      nativeAudio: true,
      nativeVideo: false,
      maxContextTokens: 32000,
      speedRating: 'medium',
      costTier: 'free',
    },
    recommended: false,
    requiresAuth: false,
    authType: 'local',
    rank: 11,
  },
];

// Provider configurations
export const PROVIDERS: ProviderConfig[] = [
  {
    id: 'google',
    name: 'Google AI',
    description: 'Gemini models with native multimodal support',
    authInstructions: 'Get your API key from Google AI Studio',
    keyUrl: 'https://aistudio.google.com/app/apikey',
    models: MODELS.filter(m => m.provider === 'google'),
  },
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'GPT-4o models with advanced capabilities',
    authInstructions: 'Get your API key from OpenAI Platform',
    keyUrl: 'https://platform.openai.com/api-keys',
    models: MODELS.filter(m => m.provider === 'openai'),
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    description: 'Claude models with superior reasoning',
    authInstructions: 'Get your API key from Anthropic Console',
    keyUrl: 'https://console.anthropic.com/settings/keys',
    models: MODELS.filter(m => m.provider === 'anthropic'),
  },
  {
    id: 'groq',
    name: 'Groq',
    description: 'Ultra-fast inference with Whisper and Llama',
    authInstructions: 'Get your API key from Groq Console',
    keyUrl: 'https://console.groq.com/keys',
    models: MODELS.filter(m => m.provider === 'groq'),
  },
  {
    id: 'local',
    name: 'Local (Ollama)',
    description: 'Run models locally with Ollama',
    authInstructions: 'Install Ollama and pull required models',
    keyUrl: 'https://ollama.ai/download',
    models: MODELS.filter(m => m.provider === 'local'),
  },
];

// Get models sorted by rank (best first)
export function getRankedModels(): ModelConfig[] {
  return [...MODELS].sort((a, b) => a.rank - b.rank);
}

/** The ID getDefaultModel() resolves to. Kept as a named constant so a model
 *  sweep cannot leave the default pointing at an entry that no longer exists. */
export const DEFAULT_MODEL_ID = 'gemini-3.8-flash';

/**
 * Retired catalogue IDs -> their current replacement.
 *
 * `selectedModelId` is PERSISTED to localStorage ('LinguistPro-Store') with no
 * `version`/`migrate` in the persist config, so a returning user carries whatever
 * ID was current when they last chose one, forever. Without this map,
 * getModelById() returns undefined for them and the picker renders "Select Model".
 *
 * Honest scope (independent review, 2026-09-14): `<ModelSelector>` is exported but
 * currently rendered NOWHERE, and `selectedModelId` is passed to executePipeline()
 * and never read, so today this map prevents a defect no user can reach. It is kept
 * because the persisted IDs are real and the component is one JSX line from being
 * mounted again. Never delete an entry — it is the only record of what an old
 * browser may still hold.
 */
export const RETIRED_MODEL_IDS: Readonly<Record<string, string>> = Object.freeze({
  'gemini-2.5-flash':  'gemini-3.8-flash',
  'gemini-3.6-flash':  'gemini-3.8-flash',
  'claude-3.5-sonnet': 'claude-sonnet-5',
  'claude-3-opus':     'claude-opus-5',
  'groq-whisper-llama': 'groq-whisper-qwen',
});

// Get default model
export function getDefaultModel(): ModelConfig {
  return MODELS.find(m => m.id === DEFAULT_MODEL_ID) || MODELS[0];
}

// Get model by ID — resolves retired IDs to their replacement
export function getModelById(id: string): ModelConfig | undefined {
  return MODELS.find(m => m.id === id)
      ?? MODELS.find(m => m.id === RETIRED_MODEL_IDS[id]);
}

// Get provider by ID
export function getProviderById(id: ModelProvider): ProviderConfig | undefined {
  return PROVIDERS.find(p => p.id === id);
}

// Check if model supports native audio
export function supportsNativeAudio(modelId: string): boolean {
  const model = getModelById(modelId);
  return model?.capabilities.nativeAudio ?? false;
}

// Get capability badge color
export function getSpeedColor(speed: ModelConfig['capabilities']['speedRating']): string {
  switch (speed) {
    case 'fast': return 'emerald';
    case 'medium': return 'amber';
    case 'slow': return 'rose';
  }
}

export function getCostColor(cost: ModelConfig['capabilities']['costTier']): string {
  switch (cost) {
    case 'free': return 'emerald';
    case 'low': return 'sky';
    case 'medium': return 'amber';
    case 'high': return 'rose';
  }
}
